import { ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Component, AfterContentInit, OnDestroy, ContentChild, Input, ViewEncapsulation, ViewChild, Output, EventEmitter } from '@angular/core';
import { OverlayTargetDirective } from '../directives/overlay-target.directive';
import { OverlayTriggerDirective } from '../directives/overlay-trigger.directive';
import { debounceTime, Subscription } from 'rxjs';
import { ActionKeys } from '../../Forms/enums/keys.enum';


@Component({
    selector: 'agpr-overlay',
    templateUrl: './overlay.component.html',
    styleUrls: ['./overlay.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class OverlayComponent implements AfterContentInit, OnDestroy {

    public static readonly CDK_OVERLAY_CONTAINER_CLASS = 'cdk-overlay-container';
    public static readonly CDK_OVERLAY_PANEL_CLASS = 'cdk-overlay-pane';

    @ContentChild(OverlayTriggerDirective, { static: true }) trigger!: OverlayTriggerDirective;
    @ContentChild(OverlayTargetDirective, { static: true }) target!: OverlayTargetDirective;
    @ViewChild('overlayDir') overlayDir!: CdkConnectedOverlay;

    @Input() initialFocusSelectors: string[] = [];
    @Input() isOpen: boolean = false;
    @Input() positions: ConnectedPosition[] = [
        // Prefer bottom-left
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        // Then bottom-right
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        // Then top-left
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        // Then top-right
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 }
    ];

    @Output() isOpenChange = new EventEmitter<boolean>();

    origin: any;

    private subscriptions: Subscription[] = [];

    ngAfterContentInit(): void {
        this.origin = this.trigger.el;
        if (this.trigger) {
            this.subscriptions.push(
                this.trigger.triggerClick.pipe(debounceTime(100)).subscribe(() => this.toggleOverlay()),
                this.trigger.closeOverlay.subscribe(() => this.closeOverlay()),

            )
        }
    }

    closeOverlay() {
        this.isOpen = false;
        this.isOpenChange.emit(this.isOpen);
    }

    toggleOverlay() {
        this.isOpen = !this.isOpen;
        this.isOpenChange.emit(this.isOpen);
        if (this.isOpen) {
            requestAnimationFrame(() => {
                const overlayElement = this.overlayDir?.overlayRef?.overlayElement;
                if (overlayElement) {
                    this.focusElement(overlayElement);
                    this.addEventListeners(overlayElement);
                }
            });
        }
    }

    addEventListeners(overlayElement: HTMLElement) {
        // check keyup instead of keydown,
        // otherwise ESC triggers the TakeOverlay dialog to close instead of the overlay popover.
        overlayElement.addEventListener('keyup', (event: KeyboardEvent) => {
            if (event.key === ActionKeys.ESCAPE) {
                event.stopImmediatePropagation();
                this.closeOverlay();
            }
        });
    }

    focusElement(overlayElement: HTMLElement) {
        if (this.initialFocusSelectors?.length) {
            for (let selector of this.initialFocusSelectors) {
                const focusable = overlayElement.querySelector(selector);
                if (focusable) {
                    (focusable as HTMLElement)?.focus();
                    break;
                }
            }
        }
    }

    ngOnDestroy() {
        this.closeOverlay();
        this.overlayDir?.overlayRef?.dispose();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
